import { isWeekend } from 'date-fns';
import { ChevronRight } from 'lucide-react';

import { ScrollableCellWithColorDot } from '@/components/data-table/cells/scrollable-cell';
import { formatHoursToTimeString } from '@/helpers';
import { normalizeColorHex } from '@/lib/utils';
import { usePermissions } from '@/modules/common';
import { OpsiumPolicy } from '@/modules/common/types/opsium-policy';

import { getProgressColor } from '../../helpers/progress-color.helper';
import { calculateProgressPercentage } from '../../helpers/progress-percentage.helper';
import { useTimeTrackingStore } from '../../store/time-tracking.store';
import { ProjectTimeTrackingSummary } from '../../types/time-tracking';
import { ProjectWeekEntries } from './project-week-entries';
import { WeekDayCell } from './week-day-cell';
import { WeekExpandableRow } from './week-expandable-row';
import { WeekSlot } from './week-slot';

interface WeekProjectRowProps {
  project: ProjectTimeTrackingSummary;
  slots: WeekSlot[];
  weekStart: Date;
}

/**
 * One row of the week grid for a single project: project label + 7 day cells
 * + a weekly-total cell. Clicking the label or any day cell toggles the row
 * open to reveal the project's time entries below.
 */
export function WeekProjectRow({ project, slots, weekStart }: WeekProjectRowProps) {
  const expandedProjects = useTimeTrackingStore(state => state.expandedProjects);
  const focusedProjectDay = useTimeTrackingStore(state => state.focusedProjectDay);
  const expandAllDays = useTimeTrackingStore(state => state.expandProjectAllDays);
  const toggleDay = useTimeTrackingStore(state => state.toggleProjectDay);
  const { hasPermission } = usePermissions();
  const canQuickAdd = hasPermission(OpsiumPolicy.WorklogsCreate);
  const projectId = project.project.projectId;
  const isExpanded = expandedProjects[projectId] ?? false;
  const focusedDay = focusedProjectDay[projectId] ?? null;
  // Project-name click → expand all days.
  const toggleAll = () => expandAllDays(projectId);

  const dayCells = slots.map(slot => {
    const found = slot.breakdown?.projectBreakdown.find(p => p.projectId === projectId);
    return {
      trackedHours: found?.trackedHours ?? 0,
      bookedHours: found?.bookedHours ?? 0,
      isBackgroundWork: found?.isBackgroundWork ?? false,
    };
  });

  const totalBooked = dayCells.reduce((sum, c) => sum + c.bookedHours, 0);

  // Per-day booked hours for THIS project, keyed by UTC calendar day
  // (YYYY-MM-DD). Only days that actually have a project breakdown entry are
  // included so the expanded entries can show booked time for this specific
  // project on each day — and fall back to "tracked only" when a day has no
  // booking for it (OPS-1520).
  const bookedHoursByDate = slots.reduce<Record<string, number>>((acc, slot) => {
    const found = slot.breakdown?.projectBreakdown.find(p => p.projectId === projectId);
    if (slot.breakdown && found) {
      acc[slot.breakdown.date.slice(0, 10)] = found.bookedHours;
    }
    return acc;
  }, {});

  return (
    <>
      <ProjectLabelCell project={project} isExpanded={isExpanded} onToggle={toggleAll} />
      {dayCells.map((cell, i) => {
        // Day key must match how the expanded entries are grouped (UTC calendar
        // day). Prefer the breakdown's UTC ISO date; fall back to the slot's
        // calendar day for days with no breakdown.
        const dayKey = getSlotDayKey(slots[i]);
        return (
          <WeekDayCell
            key={i}
            date={slots[i].date}
            projectId={projectId}
            trackedHours={cell.trackedHours}
            bookedHours={cell.bookedHours}
            isBackgroundWork={cell.isBackgroundWork}
            isWeekend={isWeekend(slots[i].date)}
            canQuickAdd={canQuickAdd}
            onCellClick={() => toggleDay(projectId, dayKey)}
          />
        );
      })}
      <ProjectWeeklyTotalCell
        trackedHours={project.trackedHours}
        bookedHours={project.bookedHours}
        totalBookedForVisibleWeek={totalBooked}
      />
      <ProjectExpandedRow
        project={project}
        weekStart={weekStart}
        isExpanded={isExpanded}
        focusedDay={focusedDay}
        bookedHoursByDate={bookedHoursByDate}
      />
    </>
  );
}

/**
 * UTC calendar-day key (`YYYY-MM-DD`) for a slot, matching how worklog entries
 * are grouped in the expanded panel. Uses the breakdown's UTC ISO date when
 * present; otherwise formats the slot's local calendar day.
 */
function getSlotDayKey(slot: WeekSlot): string {
  if (slot.breakdown) return slot.breakdown.date.slice(0, 10);
  const d = slot.date;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
}

function ProjectLabelCell({
  project,
  isExpanded,
  onToggle,
}: {
  project: ProjectTimeTrackingSummary;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex items-center gap-2.5 border-b px-3 py-3 text-left hover:bg-muted/30"
    >
      <ChevronRight
        className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${
          isExpanded ? 'rotate-90' : ''
        }`}
      />
      <div className="min-w-0 flex-1">
        <ScrollableCellWithColorDot
          text={project.project.projectName}
          image={project.project.logoUrl}
          avatarName={project.project.projectName}
          avatarSize="sm"
          alt={`${project.project.projectName} logo`}
          dotColor={normalizeColorHex(project.project.colorHex)}
          textClassName="text-[13px] font-medium"
        />
      </div>
    </button>
  );
}

function ProjectWeeklyTotalCell({
  trackedHours,
  bookedHours,
  totalBookedForVisibleWeek,
}: {
  trackedHours: number;
  bookedHours: number;
  totalBookedForVisibleWeek: number;
}) {
  const color = getProgressColor(calculateProgressPercentage(trackedHours, bookedHours));
  const widthPct =
    totalBookedForVisibleWeek > 0
      ? Math.min(100, (trackedHours / totalBookedForVisibleWeek) * 100)
      : 0;

  return (
    <div className="flex flex-col items-end justify-center gap-1 border-b border-l bg-muted/40 px-3 py-3">
      <span className="text-sm font-semibold tabular-nums">
        {formatHoursToTimeString(trackedHours)}
      </span>
      {totalBookedForVisibleWeek > 0 && (
        <div className="h-1 w-full overflow-hidden rounded-full bg-track-empty">
          <div
            className="h-full rounded-full"
            style={{ width: `${widthPct}%`, backgroundColor: color }}
          />
        </div>
      )}
      <span className="text-[10.5px] tabular-nums text-muted-foreground">
        {totalBookedForVisibleWeek > 0
          ? `of ${formatHoursToTimeString(totalBookedForVisibleWeek)}`
          : ''}
      </span>
    </div>
  );
}

/**
 * Expanded row containing per-day time entries for one project. The height
 * transition is handled by the shared `WeekExpandableRow`; `ProjectWeekEntries`
 * shows its own loader inside the expanding panel while data loads.
 */
function ProjectExpandedRow({
  project,
  weekStart,
  isExpanded,
  focusedDay,
  bookedHoursByDate,
}: {
  project: ProjectTimeTrackingSummary;
  weekStart: Date;
  isExpanded: boolean;
  /** When set (`YYYY-MM-DD`), only that day's entries are expanded (OPS-1565). */
  focusedDay: string | null;
  bookedHoursByDate: Record<string, number>;
}) {
  return (
    <WeekExpandableRow isExpanded={isExpanded}>
      {hasOpened =>
        hasOpened && (
          <div className="border-b bg-muted/20 p-4">
            <ProjectWeekEntries
              project={project}
              weekStart={weekStart}
              focusedDay={focusedDay}
              bookedHoursByDate={bookedHoursByDate}
            />
          </div>
        )
      }
    </WeekExpandableRow>
  );
}
